import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { MainLayout } from 'views/layouts'
import { BackButton, Button, StrokedButton } from 'views/components/UI/Buttons'
import ArrowLeftIcon from 'assets/icons/ArrowLeftIcon'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import _ from 'lodash'
import { useNotification } from 'lib/context/notification.context'
import { Box, IconButton, Tab, Tabs, TextField, Switch, FormControlLabel, Tooltip } from '@mui/material'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import BluesiteTable from 'views/components/UI/BluesiteTable'
import { inventoryTabsSx } from 'views/components/UI/InventoryTabs/styles'
import {
  InventoryToolbar,
  InventoryContainer,
  InventoryListContainer,
  InventoryDetailContainer,
  InventoryDetailHeader,
  InventoryDetailContent,
  InventoryHistoryContainer,
  InventoryHistoryHeader,
  InventoryHistoryContent,
  HistoryItem,
  HistoryItemHeader,
  HistoryItemContent,
  EditInventoryForm,
  EditFormRow,
  EditFormLabel,
  EditFormActions,
} from './styled'
import getInventoryByCompany from 'services/inventory/getByCompany'
import { fetchEditInventoryStatus, updateSubscriptionCompany } from 'services/inventory/updateInventory'
import { addCreditsToSubscription } from 'services/inventory/addCredits'
import { DEFAULT_LIMIT } from 'constants/global.constants'
import { getSubscriptionCompanyHistory } from 'services/inventory/getHistory'
import { superAdminRoutes } from 'router/routes'
import { TrainingSeats } from '../trainings'
import InventoryLedger from './ledger'
import useTrainingAccess from 'hooks/useTrainingAccess'

const HEADERS = [
  {
    key: 'name',
    name: 'Nombre',
  },
  {
    key: 'type',
    name: 'Tipo',
  },
  {
    key: 'credits',
    name: 'Pruebas adquiridas',
  },
  {
    key: 'tests_available',
    name: 'Pruebas por usar',
  },
  {
    key: 'status',
    name: 'Estado',
  },
]

interface InventoryRowActionProps {
  id: number
  onEdit: (inventoryId: number) => void
}

const InventoryRowAction: FC<InventoryRowActionProps> = ({ id, onEdit }) => (
  <Tooltip title="Editar inventario">
    <IconButton
      aria-label="Editar inventario"
      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        onEdit(id)
      }}
      size="small"
      sx={{ color: '#2d749c' }}
    >
      <EditOutlinedIcon fontSize="small" />
    </IconButton>
  </Tooltip>
)

interface InventoryDisplayFieldProps {
  label: string
  value: React.ReactNode
  className?: string
}

const InventoryDisplayField: FC<InventoryDisplayFieldProps> = ({ label, value, className }) => (
  <EditFormRow className={className}>
    <EditFormLabel as="span">{label}</EditFormLabel>
    <div className="inventory-field-value">{value}</div>
  </EditFormRow>
)

const Inventory: FC = () => {
  const navigate = useNavigate()
  const role: string = useSelector((state: any) => state.auth.role)
  const canManageTrainings = useTrainingAccess(role)
  const { getSuccess, getWarning, getError } = useNotification()
  const params = useParams()
  dayjs.extend(utc)

  // Función para validar si una fecha está expirada
  const isDateExpired = useCallback((date: string | Date): boolean => {
    const expirationDate = dayjs(date).startOf('day')
    const today = dayjs().startOf('day')
    return expirationDate.isBefore(today)
  }, [])

  const [idCurrentCompany, setIdCurrentCompany] = useState<any>(null)
  const [inventory, setInventory] = useState<any>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [selectedInventory, setSelectedInventory] = useState<any>(null)
  const [detailTab, setDetailTab] = useState<'info' | 'training' | 'processes' | 'consumption'>('info')
  const openInEditMode = useRef<boolean>(false)
  const [inventoryHistory, setInventoryHistory] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  // Estados para la edición de inventario
  const [isEditing, setIsEditing] = useState(false)
  const [editableInventory, setEditableInventory] = useState<any>(null)
  const [isAddingCredits, setIsAddingCredits] = useState(false)
  const [newCredits, setNewCredits] = useState<number | string>('')
  const [transactionNotes, setTransactionNotes] = useState<string>('')

  // Agregar estados para paginación del historial
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const getInventories = useCallback((page: number, limit: number) => {
    setLoading(true)

    getInventoryByCompany(parseInt(idCurrentCompany), page, limit)
      .then((response: any) => {
        // Si la respuesta es undefined o null, probablemente fue un 404
        if (!response) {
          setInventory([]);
          setTotalPages(0);
          setLoading(false);
          return;
        }

        if (response?.status === 'error') {
          console.error('Error al obtener inventarios:', response);
          setInventory([]);
          getWarning('' + (response?.message || 'Error desconocido'));
          setLoading(false);
          return;
        }

        if (response?.status === 'OK') {
          setTotalPages(response.total_pages || 1);
          const res = response.data || [];

          if (res.length === 0) {
            setInventory([]);
            setLoading(false);
            return;
          }

          const inventoriesFormated = res.map((item: any) => {
            // Formatear fecha actual en formato UTC
            const today = dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
            const expiration = item.expiration_date

            // Si la fecha de expiración ya pasó y el estado es activo, actualizamos a inactivo
            if (isDateExpired(expiration) && item.state !== false) {
              const updateData = {
                start_date: today,
                expiration_date: expiration,
                state: false,
              }

              fetchEditInventoryStatus(Number(item.id), updateData)
                .catch((err) => console.error(`Error al actualizar inventario ${item.id}:`, err))
            }

            const hasInfiniteCredits = parseInt(item.credits) > 500000000
            const creditDisplay = hasInfiniteCredits ? '∞' : item.credits
            const availableDisplay = hasInfiniteCredits ? '∞' : parseInt(item.creditsAvailable)

            return {
              id: item.subscription_company_id,
              subscription_company_id: item.subscription_company_id,
              subscription_id: item.subscription_id, // Asegurarnos de preservar este ID
              name: item.subscriptionName,
              type: item.name,
              credits: creditDisplay,
              tests_used: item.usedCredits,
              tests_available: availableDisplay,
              client_code: item.client_code,
              client_code_active: Boolean(item.client_code && item.client_code_active === true),
              client_code_generated_at: item.client_code_generated_at
                ? dayjs.utc(item.client_code_generated_at).format('DD/MM/YYYY HH:mm')
                : null,
              start_date: dayjs.utc(item.start_date).format('DD/MM/YYYY'),
              expiration_date: dayjs.utc(item.expiration_date).format('DD/MM/YYYY'),
              expiration_date_raw: dayjs.utc(item.expiration_date), // Guardamos la fecha sin formatear para edición
              status: item.state ? 'Activo' : 'Inactivo',
              state: item.state, // Guardamos el estado booleano para facilitar la lógica
            }
          })

          // Función para ordenar - primero los activos, luego por fecha de vencimiento
          const getActiveSort = (item: any) => (item.state === true ? 0 : 1)
          const getDateSort = (item: any) => (item.state === true ? dayjs.utc(item.expiration_date).valueOf() : 0)

          const sortedInventory = _.orderBy(inventoriesFormated, [getActiveSort, getDateSort])
          setInventory(sortedInventory)
        }
        setLoading(false)
      })
      .catch((error) => {
        console.error('Error en la llamada a la API de inventarios:', error);
        setInventory([]);
        getWarning('No se pudieron cargar los inventarios. Por favor, intente nuevamente.');
        setLoading(false);
      });
  }, [idCurrentCompany, getWarning, isDateExpired]);

  const handleInitData = useCallback(() => {
    getInventories(1, DEFAULT_LIMIT)
  }, [getInventories])

  useEffect(() => {
    // Obtener el ID de compañía de los parámetros de la URL
    const companyIdFromParams = params.companyId;

    if (companyIdFromParams) {
      setIdCurrentCompany(companyIdFromParams);

      // Guardar en localStorage para referencia
      localStorage.setItem('companyId', companyIdFromParams);
    } else {
      console.error('No se pudo encontrar el ID de la compañía en los parámetros');
      getWarning('No se pudo cargar el inventario: ID de compañía no encontrado');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Efecto para cargar inventarios cuando cambia el ID de compañía
  useEffect(() => {
    if (idCurrentCompany) {
      handleInitData();
    }
  }, [idCurrentCompany, handleInitData]);

  // Actualizar la función getInventoryHistory para procesar correctamente la respuesta
  const getInventoryHistory = useCallback((subscriptionCompanyId: number, page = 1, limit = 10) => {
    if (!subscriptionCompanyId) return;

    setLoadingHistory(true);

    getSubscriptionCompanyHistory({
      subscriptionCompanyId,
      page,
      limit
    })
      .then((response) => {
        if (response?.status === 'success' && response?.data?.logs) {
          // Transformar los datos recibidos al formato esperado
          const formattedHistory = response.data.logs.map((item) => {
            // Usar directamente la propiedad formatted_action del objeto response
            const actionType = item.formatted_action || 'Modificación';

            // Usar las notas directamente del API
            let details = item.notes || '';

            // Solo añadir información adicional sobre los cambios si no hay notas
            if (item.previous_state && item.new_state && !details) {
              const changes = [];

              // Cambio de estado
              if (item.previous_state.state !== item.new_state.state) {
                changes.push(`Estado: ${item.previous_state.state ? 'Activo' : 'Inactivo'} → ${item.new_state.state ? 'Activo' : 'Inactivo'}`);
              }

              // Cambio en créditos
              if (item.previous_state.credits !== item.new_state.credits) {
                const creditDiff = item.new_state.credits - item.previous_state.credits;
                changes.push(`Créditos: ${item.previous_state.credits} → ${item.new_state.credits} (${creditDiff > 0 ? '+' : ''}${creditDiff})`);
              }

              // Cambio en fecha de expiración
              if (item.previous_state.expiration_date !== item.new_state.expiration_date) {
                changes.push(`Fecha de expiración: ${dayjs(item.previous_state.expiration_date).format('DD/MM/YYYY')} → ${dayjs(item.new_state.expiration_date).format('DD/MM/YYYY')}`);
              }

              if (changes.length > 0) {
                details = `Cambios: ${changes.join(', ')}`;
              }
            }

            return {
              id: item.id,
              date: dayjs(item.created_at).format('DD/MM/YYYY HH:mm:ss'),
              user: item.user?.name || 'Sistema',
              action: actionType,
              details: details || 'No hay detalles disponibles'
            };
          });

          setInventoryHistory(formattedHistory);

          // Establecer la información de paginación
          setHistoryTotalPages(response.data.pagination.total_pages || 1);
          setHistoryCurrentPage(response.data.pagination.page || 1);
        } else {
          setInventoryHistory([]);
        }
      })
      .catch((error) => {
        console.error('Error al obtener el historial:', error);
        getWarning('No se pudo cargar el historial de modificaciones');
        setInventoryHistory([]);
      })
      .finally(() => {
        setLoadingHistory(false);
      });
  }, [getWarning]);

  // Función para manejar la paginación del historial
  const handleHistoryPagination = (page: number) => {
    if (selectedInventory && selectedInventory.subscription_company_id) {
      getInventoryHistory(selectedInventory.subscription_company_id, page);
    }
  };

  // Modificar el useEffect que se ejecuta cuando cambia el inventario seleccionado
  useEffect(() => {
    if (selectedInventory) {
      // Obtener el historial real del API si tenemos un subscription_company_id
      if (selectedInventory.subscription_company_id) {
        getInventoryHistory(selectedInventory.subscription_company_id);
      } else {
        setInventoryHistory([]);
      }

      setEditableInventory({
        ...selectedInventory,
        expiration_date: selectedInventory.expiration_date_raw,
        client_code_active:
          selectedInventory.client_code_active ?? selectedInventory.state,
      })
    } else {
      setInventoryHistory([])
      setEditableInventory(null)
    }
    setIsEditing(openInEditMode.current)
    openInEditMode.current = false
    setIsAddingCredits(false)
    setNewCredits('')
  }, [selectedInventory, getInventoryHistory])

  const handlePagination = (page: number) => {
    setCurrentPage(page)
    getInventories(page, DEFAULT_LIMIT)
  }

  const handleSelectInventory = (item: any) => {
    openInEditMode.current = false
    setDetailTab('info')
    setSelectedInventory(item)
  }

  const handleEditInventoryFromTable = (inventoryId: number): void => {
    const item = inventory.find((entry: { id: number }) => entry.id === inventoryId)
    if (!item) return
    openInEditMode.current = true
    setDetailTab('info')
    setSelectedInventory(item)
  }

  const handleStartEditing = () => {
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditableInventory({
      ...selectedInventory,
      expiration_date: selectedInventory.expiration_date_raw,
      client_code_active:
        selectedInventory.client_code_active ?? selectedInventory.state,
    })
  }

  const handleSaveEdit = () => {
    // Validamos que la fecha exista y sea válida
    if (!editableInventory.expiration_date || !dayjs(editableInventory.expiration_date).isValid()) {
      getWarning('La fecha de expiración no es válida')
      return
    }

    // Validamos que la fecha no sea anterior a hoy
    const today = dayjs().startOf('day')
    const expirationDate = dayjs(editableInventory.expiration_date).startOf('day')

    // Si está intentando activar un inventario con fecha vencida
    if (editableInventory.state && expirationDate.isBefore(today)) {
      getWarning('No puede activar un inventario con fecha de vencimiento anterior a la fecha actual')
      return
    }

    // Crear un objeto vacío para almacenar solo los datos que han cambiado
    if (!editableInventory.name || !editableInventory.name.trim()) {
      getWarning('El nombre del inventario es requerido')
      return
    }

    const editableCredits = Number(editableInventory.credits)

    if (
      isNaN(editableCredits) ||
      !Number.isInteger(editableCredits) ||
      editableCredits <= 0
    ) {
      getWarning('Las pruebas adquiridas deben ser un número entero positivo')
      return
    }

    const updateData: any = {};

    // Comprobar si la fecha de expiración ha cambiado
    const originalExpirationDate = dayjs(selectedInventory.expiration_date_raw).format('YYYY-MM-DD');
    const newExpirationDate = dayjs(editableInventory.expiration_date).format('YYYY-MM-DD');

    if (originalExpirationDate !== newExpirationDate) {
      updateData.expiration_date = dayjs(editableInventory.expiration_date).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    }

    // Comprobar si el estado ha cambiado
    if (selectedInventory.state !== editableInventory.state) {
      updateData.state = editableInventory.state;
    }

    if (selectedInventory.name !== editableInventory.name) {
      updateData.name = editableInventory.name;
    }

    const currentClientCodeActive =
      selectedInventory.client_code_active ?? selectedInventory.state;

    if (currentClientCodeActive !== editableInventory.client_code_active) {
      updateData.client_code_active = editableInventory.client_code_active;
    }

    const currentCredits = Number(selectedInventory.credits);
    const newCreditsValue = editableCredits;

    if (currentCredits !== newCreditsValue) {
      updateData.credits = newCreditsValue;
    }

    // Si no hay cambios, mostramos un mensaje y salimos
    if (Object.keys(updateData).length === 0) {
      getWarning('No se han detectado cambios que actualizar');
      return;
    }

    // Aseguramos que el ID sea un número válido
    // Para la API de subscription_company, debemos usar el ID de suscripción de compañía
    const subscriptionCompanyId = selectedInventory.subscription_company_id;

    if (!subscriptionCompanyId || isNaN(Number(subscriptionCompanyId))) {
      getWarning('ID de suscripción de compañía no válido');
      return;
    }

    setLoading(true)

    // Llamada al servicio de actualización usando el nuevo endpoint PATCH
    updateSubscriptionCompany(Number(subscriptionCompanyId), updateData)
      .then(() => {
        // Actualizamos el inventario seleccionado
        const updatedInventory = {
          ...selectedInventory,
          name: editableInventory.name,
          credits: editableInventory.credits,
          tests_available: Number(editableInventory.credits) - Number(selectedInventory.tests_used),
          expiration_date: dayjs(editableInventory.expiration_date).format('DD/MM/YYYY'),
          expiration_date_raw: editableInventory.expiration_date,
          state: editableInventory.state,
          client_code_active: editableInventory.client_code_active,
          status: editableInventory.state ? 'Activo' : 'Inactivo',
        }

        setSelectedInventory(updatedInventory)

        // Actualizamos la lista de inventarios
        if (inventory) {
          const updatedInventories = inventory.map((item: any) =>
            item.inventoryId === selectedInventory.inventoryId ? updatedInventory : item
          )
          setInventory(updatedInventories)
        }

        setIsEditing(false)
        getSuccess('Inventario actualizado exitosamente')

        // Recargar listado de inventarios para mantener datos actualizados
        getInventories(currentPage, DEFAULT_LIMIT)

        if (selectedInventory.subscription_company_id) {
          getInventoryHistory(selectedInventory.subscription_company_id)
        }
      })
      .catch((error) => {
        console.error('Error al actualizar inventario:', error)
        let errorMessage = 'Error al actualizar el inventario'

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }

        getWarning(errorMessage)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleAddCredits = () => {
    setIsAddingCredits(true)
  }

  const handleCancelAddCredits = () => {
    setIsAddingCredits(false)
    setNewCredits('')
    setTransactionNotes('')
  }

  const handleSaveNewCredits = () => {
    // Validamos que sea un número entero positivo
    const creditsToAdd = Number(newCredits);
    if (!newCredits || isNaN(creditsToAdd) || !Number.isInteger(creditsToAdd) || creditsToAdd <= 0) {
      getWarning('Debe ingresar un número entero positivo de créditos mayor a 0')
      return
    }

    // Validamos que la fecha de expiración no sea pasada
    // Usamos endOf('day') para considerar hasta las 23:59:59 del día de expiración
    const expirationDate = dayjs(selectedInventory.expiration_date_raw).endOf('day')
    // Usamos ahora para comparar con la fecha y hora actual
    const now = dayjs()

    // Verificamos si la fecha de expiración (al final del día) ya pasó
    if (expirationDate.isBefore(now)) {
      getWarning('No se pueden añadir créditos a un inventario con fecha de expiración vencida')
      return
    }

    // Obtener el ID correcto para el endpoint /subscription/:subscription_id/credits
    // Necesitamos subscription_id según la documentación
    let subscriptionId = null;

    // Intentamos obtener el ID usando el campo correcto según el API
    if (selectedInventory.subscription_id && !isNaN(Number(selectedInventory.subscription_id))) {
      subscriptionId = Number(selectedInventory.subscription_id);
    } else if (selectedInventory.id && !isNaN(Number(selectedInventory.id))) {
      subscriptionId = Number(selectedInventory.id);
    }

    if (!subscriptionId || !Number.isInteger(subscriptionId) || subscriptionId <= 0) {
      getWarning('ID de suscripción no válido. Debe ser un número entero positivo.')
      console.error('ID inválido:', subscriptionId, typeof subscriptionId)
      return
    }

    setLoading(true)

    // Llamar al servicio de agregar créditos
    addCreditsToSubscription({
      subscriptionId: subscriptionId,
      credits: creditsToAdd,
      notes: transactionNotes || 'Adición de créditos desde panel de administración',
    })
      .then((response: any) => {
        const updatedSubscription = response?.data?.subscription

        getSuccess(`Se han añadido ${creditsToAdd} créditos correctamente`)

        // Actualizar el inventario en la lista
        getInventories(currentPage, DEFAULT_LIMIT)

        if (updatedSubscription) {
          const hasInfiniteCredits = Number(updatedSubscription.credits) > 500000000
          setSelectedInventory({
            ...selectedInventory,
            ...updatedSubscription,
            credits: hasInfiniteCredits ? '∞' : updatedSubscription.credits,
            tests_available: hasInfiniteCredits
              ? '∞'
              : Number(updatedSubscription.credits) - Number(updatedSubscription.tests_used || 0),
            client_code_active:
              updatedSubscription.client_code_active ?? selectedInventory.client_code_active ?? selectedInventory.state,
            client_code_generated_at: updatedSubscription.client_code_generated_at
              ? dayjs.utc(updatedSubscription.client_code_generated_at).format('DD/MM/YYYY HH:mm')
              : selectedInventory.client_code_generated_at,
            start_date: selectedInventory.start_date,
            expiration_date: selectedInventory.expiration_date,
            expiration_date_raw: selectedInventory.expiration_date_raw,
            status: updatedSubscription.state ? 'Activo' : 'Inactivo',
          })
        }

        // Añadir entrada al historial con las notas correctas
        const newHistoryItem = {
          id: Date.now(),
          date: dayjs().format('DD/MM/YYYY HH:mm:ss'),
          user: 'Admin Sistema',
          action: 'Adición de créditos',
          details: transactionNotes || 'Adición de créditos desde panel de administración'
        };
        setInventoryHistory([newHistoryItem, ...inventoryHistory]);

        // Resetear el estado
        setNewCredits('')
        setTransactionNotes('')
        setIsAddingCredits(false)

        // Actualizar el historial para reflejar los cambios del backend
        if (selectedInventory.subscription_company_id) {
          setTimeout(() => {
            getInventoryHistory(selectedInventory.subscription_company_id);
          }, 1000); // Esperar 1 segundo para asegurar que el backend ha procesado la operación
        }
      })
      .catch((error) => {
        console.error('Error al añadir créditos:', error)
        getError(error.response?.data?.message || 'Error al añadir créditos. Intente nuevamente.')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <MainLayout>
      <InventoryToolbar>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            className="inventory-back-button"
            onClick={() => selectedInventory ? setSelectedInventory(null) : navigate(-1)}
            type="button"
            leadingIcon={<ArrowLeftIcon />}
            style={{ marginRight: '15px' }}
          >
            Atrás
          </Button>
          <h5>{selectedInventory ? selectedInventory.name : 'Inventarios'}</h5>
        </div>
        {!selectedInventory && (
          <Button
            className="inventory-new-button"
            onClick={() => navigate(superAdminRoutes.NewInventory.replace(':companyId', idCurrentCompany.toString()))}
            type="button"
          >
            Nuevo inventario
          </Button>
        )}
      </InventoryToolbar>

      <InventoryContainer>
        {!selectedInventory && <InventoryListContainer>
          <div style={{ width: '100%', position: 'relative' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>Cargando inventarios...</p>
              </div>
            ) : inventory.length > 0 ? (
              <BluesiteTable
                data={inventory}
                headers={HEADERS}
                searchBar={false}
                searchLabel=""
                totalPages={totalPages}
                currentPage={currentPage}
                handlePagination={handlePagination}
                onRowClick={handleSelectInventory}
                ActionComponent={InventoryRowAction}
                extraDataAction={{ onEdit: handleEditInventoryFromTable }}
                minimumColumnWidth={120}
                tableStyle="inventory-management"
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>No hay inventarios disponibles para esta compañía.</p>
                <p>Puede crear un nuevo inventario utilizando el botón "Nuevo inventario".</p>
              </div>
            )}
          </div>
        </InventoryListContainer>}

        {selectedInventory && <InventoryDetailContainer>
          <Box sx={{ background: '#fff', borderRadius: '10px', px: 1 }}>
            <Tabs value={detailTab} onChange={(_event: React.SyntheticEvent, value: 'info' | 'training' | 'processes' | 'consumption') => setDetailTab(value)} variant="scrollable" scrollButtons="auto" aria-label="Secciones del inventario" sx={inventoryTabsSx}>
              <Tab label="Información del inventario" value="info" />
              {canManageTrainings && ['Silver', 'Gold', 'Ilimitado', 'Ilimitado Integración'].includes(selectedInventory.type) && <Tab label="Cupos de entrenamiento" value="training" />}
              <Tab label="Procesos" value="processes" />
              <Tab label="Consumo del inventario" value="consumption" />
            </Tabs>
          </Box>
          {detailTab === 'info' ? (
            <>
              <Box sx={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)' }}>
              <InventoryDetailHeader>
                <h6>Detalle del inventario</h6>
                <div>
                  {!isEditing && !isAddingCredits && (
                    <>
                      <Button className="inventory-detail-primary-button" onClick={handleStartEditing} style={{ marginRight: '10px' }}>
                        Editar Inventario
                      </Button>
                      {!selectedInventory.type.toLowerCase().includes('ilimitado') && (
                        <Button className="inventory-detail-secondary-button" onClick={handleAddCredits}>
                          Agregar Créditos
                        </Button>
                      )}
                      {role === 'super_admin' && (
                        <StrokedButton
                          onClick={() =>
                            navigate(
                              superAdminRoutes.Certifications
                                .replace(':companyId', String(idCurrentCompany))
                                .replace(
                                  ':subscriptionCompanyId',
                                  String(selectedInventory.subscription_company_id),
                                ),
                            )
                          }
                          style={{ marginLeft: '10px' }}
                        >
                          Gestionar procesos
                        </StrokedButton>
                      )}
                    </>
                  )}
                </div>
              </InventoryDetailHeader>

              <InventoryDetailContent>
                {isEditing ? (
                  <EditInventoryForm>
                    <EditFormRow className="field-6">
                      <EditFormLabel htmlFor="inventory-name">Nombre</EditFormLabel>
                      <TextField
                        id="inventory-name"
                        type="text"
                        value={editableInventory.name}
                        onChange={(e) =>
                          setEditableInventory({
                            ...editableInventory,
                            name: e.target.value,
                          })
                        }
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    </EditFormRow>
                    <InventoryDisplayField className="field-3" label="Tipo" value={selectedInventory.type} />
                    <EditFormRow className="field-3">
                      <EditFormLabel as="span">Estado</EditFormLabel>
                      <div className="inventory-switch-value">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={editableInventory.state}
                              onChange={(e) => {
                                const today = dayjs().startOf('day')
                                const expirationDate = dayjs(editableInventory.expiration_date).startOf('day')

                                if (e.target.checked && expirationDate.isBefore(today)) {
                                  getWarning('No puede activar un inventario con fecha de vencimiento anterior a la fecha actual')
                                  return
                                }

                                setEditableInventory({ ...editableInventory, state: e.target.checked })
                              }}
                              color="primary"
                              disabled={
                                dayjs(editableInventory.expiration_date).isBefore(dayjs().startOf('day')) &&
                                !editableInventory.state
                              }
                            />
                          }
                          label={editableInventory.state ? 'Activo' : 'Inactivo'}
                        />
                      </div>
                    </EditFormRow>
                    <EditFormRow className="field-2">
                      <EditFormLabel htmlFor="inventory-credits">Pruebas adquiridas</EditFormLabel>
                      <TextField
                        id="inventory-credits"
                        type="number"
                        value={editableInventory.credits}
                        onChange={(e) =>
                          setEditableInventory({
                            ...editableInventory,
                            credits: e.target.value,
                          })
                        }
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    </EditFormRow>
                    <InventoryDisplayField className="field-2" label="Pruebas usadas" value={selectedInventory.tests_used} />
                    <InventoryDisplayField className="field-2" label="Pruebas disponibles" value={selectedInventory.tests_available} />
                    <InventoryDisplayField className="field-2" label="Fecha de activación" value={selectedInventory.start_date} />
                    <EditFormRow className="field-2">
                      <EditFormLabel htmlFor="inventory-expiration">Fecha de vencimiento</EditFormLabel>
                      <TextField
                        id="inventory-expiration"
                        type="date"
                        value={dayjs(editableInventory.expiration_date).format('YYYY-MM-DD')}
                        onChange={(e) => {
                          const selectedDate = dayjs(e.target.value)
                          const today = dayjs().startOf('day')

                          // Si la fecha seleccionada es anterior a hoy, automáticamente desactivamos el inventario
                          const newState = selectedDate.isBefore(today) ? false : editableInventory.state

                          setEditableInventory({
                            ...editableInventory,
                            expiration_date: selectedDate,
                            state: newState,
                          })

                          // Si se seleccionó una fecha pasada, mostrar advertencia
                          if (selectedDate.isBefore(today)) {
                            getWarning(
                              'La fecha seleccionada es anterior a la fecha actual. El inventario será inactivado automáticamente.'
                            )
                          }
                        }}
                        inputProps={{
                          min: dayjs().subtract(100, 'years').format('YYYY-MM-DD'), // Permitimos fechas pasadas pero con advertencia
                        }}
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    </EditFormRow>
                    <EditFormRow className="field-2">
                      <EditFormLabel as="span">Código de cliente</EditFormLabel>
                      <div className="inventory-switch-value">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={editableInventory.client_code_active ?? editableInventory.state}
                              onChange={(e) => {
                                setEditableInventory({
                                  ...editableInventory,
                                  client_code_active: e.target.checked,
                                })
                              }}
                              color="primary"
                            />
                          }
                          label={(editableInventory.client_code_active ?? editableInventory.state) ? 'Activo' : 'Inactivo'}
                        />
                      </div>
                    </EditFormRow>
                    <EditFormActions>
                      <BackButton hideIcon onClick={handleCancelEdit} text="Cancelar" />
                      <Button className="inventory-detail-primary-button" onClick={handleSaveEdit}>
                        Guardar Cambios
                      </Button>
                    </EditFormActions>
                  </EditInventoryForm>
                ) : isAddingCredits ? (
                  <EditInventoryForm>
                    <InventoryDisplayField
                      className="field-6"
                      label="Inventario"
                      value={`${selectedInventory.name} (${selectedInventory.type})`}
                    />
                    <InventoryDisplayField className="field-2" label="Créditos actuales" value={selectedInventory.credits} />
                    <InventoryDisplayField className="field-2" label="Créditos usados" value={selectedInventory.tests_used} />
                    <InventoryDisplayField className="field-2" label="Disponibles" value={selectedInventory.tests_available} />
                    <EditFormRow className="field-2">
                      <EditFormLabel htmlFor="inventory-new-credits">Créditos a agregar</EditFormLabel>
                      <TextField
                        id="inventory-new-credits"
                        type="number"
                        value={newCredits}
                        onChange={(e) => setNewCredits(e.target.value)}
                        placeholder="Ingrese cantidad"
                        fullWidth
                        size="small"
                        variant="outlined"
                      />
                    </EditFormRow>
                    <EditFormRow className="field-10">
                      <EditFormLabel htmlFor="inventory-transaction-notes">Notas de transacción (opcional)</EditFormLabel>
                      <TextField
                        id="inventory-transaction-notes"
                        type="text"
                        value={transactionNotes}
                        onChange={(e) => setTransactionNotes(e.target.value)}
                        placeholder="Detalles o motivo de la adición"
                        fullWidth
                        size="small"
                        variant="outlined"
                        multiline
                        rows={2}
                      />
                    </EditFormRow>
                    <EditFormActions>
                      <BackButton hideIcon onClick={handleCancelAddCredits} text="Cancelar" />
                      <Button className="inventory-detail-primary-button" onClick={handleSaveNewCredits}>
                        Agregar Créditos
                      </Button>
                    </EditFormActions>
                  </EditInventoryForm>
                ) : (
                  <EditInventoryForm>
                    <InventoryDisplayField className="field-6" label="Nombre" value={selectedInventory.name} />
                    <InventoryDisplayField className="field-3" label="Tipo" value={selectedInventory.type} />
                    <InventoryDisplayField className="field-3" label="Estado" value={selectedInventory.status} />
                    <InventoryDisplayField className="field-2" label="Pruebas adquiridas" value={selectedInventory.credits} />
                    <InventoryDisplayField className="field-2" label="Pruebas usadas" value={selectedInventory.tests_used} />
                    <InventoryDisplayField className="field-2" label="Pruebas disponibles" value={selectedInventory.tests_available} />
                    <InventoryDisplayField className="field-2" label="Fecha de activación" value={selectedInventory.start_date} />
                    <InventoryDisplayField className="field-2" label="Fecha de vencimiento" value={selectedInventory.expiration_date} />
                    <InventoryDisplayField
                      className="field-2"
                      label="Estado del código"
                      value={selectedInventory.client_code_active ? 'Activo' : 'Inactivo'}
                    />
                    <InventoryDisplayField
                      className="field-8"
                      label="Código de cliente"
                      value={
                        <>
                          <span>{selectedInventory.client_code || 'Sin código'}</span>
                          <Tooltip title="Copiar código de cliente">
                            <span className="inventory-code-action">
                              <IconButton
                                aria-label="Copiar código de cliente"
                                size="small"
                                disabled={!selectedInventory.client_code}
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(selectedInventory.client_code)
                                    getSuccess('Código copiado al portapapeles')
                                  } catch (error) {
                                    console.error('No se pudo copiar el código', error)
                                    getWarning('No se pudo copiar el código de cliente.')
                                  }
                                }}
                              >
                                <ContentCopyOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      }
                    />
                    <InventoryDisplayField
                      label="Fecha del código"
                      value={selectedInventory.client_code_generated_at || 'Sin fecha'}
                    />
                  </EditInventoryForm>
                )}
              </InventoryDetailContent>
              </Box>

              <InventoryHistoryContainer>
                <InventoryHistoryHeader>
                  <h6>Historial de modificaciones</h6>
                </InventoryHistoryHeader>

                <InventoryHistoryContent>
                  {loadingHistory ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <p>Cargando historial...</p>
                    </div>
                  ) : inventoryHistory.length > 0 ? (
                    <>
                      {inventoryHistory.map((item) => (
                        <HistoryItem key={item.id}>
                          <HistoryItemHeader>
                            <span>{item.date}</span>
                            <strong>{item.action}</strong>
                          </HistoryItemHeader>
                          <HistoryItemContent>
                            <span>
                              <strong>Usuario:</strong> {item.user}
                            </span>
                            <span>{item.details}</span>
                          </HistoryItemContent>
                        </HistoryItem>
                      ))}

                      {historyTotalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                          <div className="pagination">
                            {Array.from({ length: historyTotalPages }, (unused, i) => i + 1).map((page) => (
                              <button
                                key={`history-page-${page}`}
                                onClick={() => handleHistoryPagination(page)}
                                className={page === historyCurrentPage ? 'active' : ''}
                                style={{
                                  margin: '0 5px',
                                  padding: '5px 10px',
                                  background: page === historyCurrentPage ? '#2D749C' : '#f5f5f5',
                                  color: page === historyCurrentPage ? 'white' : 'black',
                                  border: '1px solid #ddd',
                                  borderRadius: '3px',
                                  cursor: 'pointer'
                                }}
                              >
                                {page}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p>No hay historial de modificaciones disponible</p>
                  )}
                </InventoryHistoryContent>
              </InventoryHistoryContainer>
            </>
          ) : detailTab === 'training' && canManageTrainings ? (
            <TrainingSeats inventoryId={String(selectedInventory.subscription_company_id)} embedded />
          ) : detailTab === 'processes' ? (
            <InventoryLedger inventoryId={selectedInventory.subscription_company_id} view="processes" />
          ) : (
            <InventoryLedger inventoryId={selectedInventory.subscription_company_id} view="consumption" />
          )}
        </InventoryDetailContainer>}
      </InventoryContainer>
    </MainLayout>
  )
}

export default Inventory
